import { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useSocket } from '@/lib/socket';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/lib/types';

interface Location {
  city: string;
  state: string;
}

interface LocationContextType {
  locations: Location[];
  selectedLocation: string | null;
  setSelectedLocation: (location: string) => void;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export function LocationProvider({ children }: { children: ReactNode }) {
  const socket = useSocket();
  const { currentUser } = useAuth();
  
  const [allLocations, setAllLocations] = useState<Location[]>([
    { city: "All", state: "USA" },
    { city: "Auburn", state: "WA" },
    { city: "Grand Prairie", state: "TX" }
  ]);
  
  // Filter locations based on user permissions
  const locations = useMemo(() => {
    if (!currentUser) return allLocations;
    
    const isAdmin = currentUser.roles.includes(UserRole.ADMIN);
    const hasAllLocationsAccess = currentUser.roles.includes(UserRole.ALL_LOCATIONS);
    
    // If user is admin or has all locations access, show all locations
    if (isAdmin || hasAllLocationsAccess) {
      return allLocations;
    }
    
    // Otherwise, filter locations based on user's specific location permissions
    const filteredLocations = allLocations.filter(location => {
      // Only show "All, USA" if user has ALL_LOCATIONS role
      if (location.city === "All" && location.state === "USA") {
        return false;
      }
      
      if (location.city === "Auburn" && location.state === "WA") {
        return currentUser.roles.includes(UserRole.LOCATION_AUBURN_WA);
      }
      
      if (location.city === "Grand Prairie" && location.state === "TX") {
        return currentUser.roles.includes(UserRole.LOCATION_GRAND_PRAIRIE_TX);
      }
      
      // For any other locations, check if user has access to that specific location
      // This would need to be expanded if more locations are added with specific roles
      return false;
    });
    
    // If no locations are available after filtering, include the first location they have access to
    if (filteredLocations.length === 0) {
      if (currentUser.roles.includes(UserRole.LOCATION_AUBURN_WA)) {
        return [{ city: "Auburn", state: "WA" }];
      } else if (currentUser.roles.includes(UserRole.LOCATION_GRAND_PRAIRIE_TX)) {
        return [{ city: "Grand Prairie", state: "TX" }];
      }
      // If they don't have access to any specific location, return an empty array
      return [];
    }
    
    return filteredLocations;
  }, [allLocations, currentUser]);
  
  const cookieLocation = Cookies.get('selectedLocation');
  const [selectedLocation, setSelectedLocation] = useState<string | null>(cookieLocation || null);
  const navigate = useNavigate();
  const location = useRouterLocation();

  // Validate the selected location when user permissions change
  useEffect(() => {
    if (selectedLocation && currentUser) {
      const isAdmin = currentUser.roles.includes(UserRole.ADMIN);
      const hasAllLocationsAccess = currentUser.roles.includes(UserRole.ALL_LOCATIONS);
      
      // Check if the current selected location is still valid for this user
      let isValidLocation = false;
      
      if (selectedLocation === 'All, USA') {
        isValidLocation = isAdmin || hasAllLocationsAccess;
      } else if (selectedLocation === 'Auburn, WA') {
        isValidLocation = isAdmin || hasAllLocationsAccess || currentUser.roles.includes(UserRole.LOCATION_AUBURN_WA);
      } else if (selectedLocation === 'Grand Prairie, TX') {
        isValidLocation = isAdmin || hasAllLocationsAccess || currentUser.roles.includes(UserRole.LOCATION_GRAND_PRAIRIE_TX);
      }
      
      // If current location is not valid, reset to the first available location
      if (!isValidLocation && locations.length > 0) {
        const firstAvailable = `${locations[0].city}, ${locations[0].state}`;
        setSelectedLocation(firstAvailable);
        Cookies.set('selectedLocation', firstAvailable, { expires: 30 });
      }
    }
  }, [currentUser, locations]);

  useEffect(() => {
    if (!cookieLocation) {
      const path = location.pathname;
      const matches = path.match(/\/([a-z]{2})\/([a-z-]+)\/dashboard/);
      if (matches) {
        const [, state, city] = matches;
        const formattedCity = city.split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
        const formattedState = state.toUpperCase();
        setSelectedLocation(`${formattedCity}, ${formattedState}`);
        Cookies.set('selectedLocation', `${formattedCity}, ${formattedState}`, { expires: 30 });
      } else if (path.startsWith('/dashboard')) {
        // Only set to All, USA if user has access to it
        if (currentUser && (currentUser.roles.includes(UserRole.ADMIN) || 
            currentUser.roles.includes(UserRole.ALL_LOCATIONS))) {
          setSelectedLocation('All, USA');
          Cookies.set('selectedLocation', 'All, USA', { expires: 30 });
        } else if (locations.length > 0) {
          // Otherwise set to first available location
          const firstAvailable = `${locations[0].city}, ${locations[0].state}`;
          setSelectedLocation(firstAvailable);
          Cookies.set('selectedLocation', firstAvailable, { expires: 30 });
        }
      }
    }
  }, [location.pathname, cookieLocation, currentUser, locations]);

  const handleLocationChange = (newLocation: string) => {
    // Check if user has access to this location
    if (currentUser) {
      const isAdmin = currentUser.roles.includes(UserRole.ADMIN);
      const hasAllLocationsAccess = currentUser.roles.includes(UserRole.ALL_LOCATIONS);
      
      if (newLocation === 'All, USA' && !isAdmin && !hasAllLocationsAccess) {
        // If user doesn't have access to all locations, don't change
        return;
      }
      
      if (newLocation === 'Auburn, WA') {
        const hasLocationAccess = currentUser.roles.includes(UserRole.LOCATION_AUBURN_WA);
        if (!isAdmin && !hasAllLocationsAccess && !hasLocationAccess) {
          return;
        }
      }
      
      if (newLocation === 'Grand Prairie, TX') {
        const hasLocationAccess = currentUser.roles.includes(UserRole.LOCATION_GRAND_PRAIRIE_TX);
        if (!isAdmin && !hasAllLocationsAccess && !hasLocationAccess) {
          return;
        }
      }
    }
    
    setSelectedLocation(newLocation);
    Cookies.set('selectedLocation', newLocation, { expires: 30 });
    
    const currentPath = location.pathname.split('/').pop() || '';
    const basePath = currentPath === 'dashboard' ? '' : `/${currentPath}`;
    
    navigate(`dashboard${basePath}`);
  };

  // Handle socket connection and data fetching
  useEffect(() => {
    if (!socket) return;

    const handleNetSuiteData = (data: any[]) => {
      const uniqueLocations = new Set<string>();
      data.forEach(item => {
        if (item.location) {
          uniqueLocations.add(item.location);
        }
      });

      const locationArray = Array.from(uniqueLocations).map(loc => {
        const lastSpaceIndex = loc.lastIndexOf(' ');
        return { 
          city: loc.slice(0, lastSpaceIndex).trim(), 
          state: loc.slice(lastSpaceIndex + 1).trim() 
        };
      });

      // Filter out Auburn, WA and Grand Prairie, TX since they're already in the default state
      const filteredLocations = locationArray.filter(loc => 
        !(loc.city === "Auburn" && loc.state === "WA") && 
        !(loc.city === "Grand Prairie" && loc.state === "TX")
      );

      setAllLocations([
        { city: "All", state: "USA" },
        { city: "Auburn", state: "WA" },
        { city: "Grand Prairie", state: "TX" },
        ...filteredLocations
      ]);
    };

    // Set up socket event handlers
    socket.on('netsuiteData', handleNetSuiteData);
    
    // Request initial data immediately when socket is ready
    const requestData = () => {
      if (socket.connected) {
        socket.emit('requestNetSuiteData');
      }
    };

    // Request data when socket connects
    socket.on('connect', requestData);
    
    // Initial request if socket is already connected
    if (socket.connected) {
      requestData();
    }

    return () => {
      socket.off('netsuiteData', handleNetSuiteData);
      socket.off('connect', requestData);
    };
  }, [socket]);

  return (
    <LocationContext.Provider value={{ 
      locations, 
      selectedLocation, 
      setSelectedLocation: handleLocationChange 
    }}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocationContext() {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocationContext must be used within a LocationProvider');
  }
  return context;
} 